import { Inject, Injectable, OnDestroy } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import {
  FundsSearchItem,
  SearchFundsItem,
} from '@marketing/subscriptions-manage/subscription-manage.interface';
import { Product } from '@models';
import { FavoritesService } from '@products/services/favorites.service';
import { GraphQLFundDataService } from '@products/services/graphql-fund-data.service';
import { NA, SINGLECLASS } from '@products/utils/constants/product.constants';
import { MapperFactory } from '@products/utils/mappers/mapper-factory';
import {
  mapProductToProductData,
  ProductMapper,
} from '@products/utils/mappers/product.type.mapper';
import { MapperParams } from '@products/utils/mappers/type.mapper';
import {
  AppStateService,
  PersonalisationAPIService,
  SiteConfigService,
} from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import {
  FundShareClassId,
  PersonalisationFavoriteData,
  PersonalisationPersonalDataProduct,
  PersonalisationPersonalDataWithProducts,
  ProductData,
  ProductDTO,
} from '@types';
import { Logger } from '@utils/logger';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import USServicingMyInvestmentsQuery from '../graphql/us-servicing-my-investments.graphql';
import { WINDOW } from '@ng-web-apis/common';

const logger = Logger.getLogger('USServicingService');

@Injectable({
  providedIn: 'root',
})
export class USServicingService implements OnDestroy {
  private unsubscribe$: Subject<void> = new Subject<void>();
  private usServicingPageTriggered: string;

  constructor(
    private siteConfigService: SiteConfigService,
    private appStateService: AppStateService,
    private personalisationService: PersonalisationAPIService,
    private favoritesService: FavoritesService,
    private translateService: TranslateService,
    private fundDataService: GraphQLFundDataService,
    private mapperFactory: MapperFactory,
    @Inject(WINDOW) readonly windowRef: Window
  ) {}

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Returns string from PageAnalyticsMetadata array item
   * @param data - PageAnalyticsMetadata Array item
   */
  private joinAnalyticsMetadata(data: []): string {
    if (data && data?.length > 0) {
      return data.join(',');
    }
  }

  public getFavorites$ = (): Observable<PersonalisationPersonalDataProduct[]> =>
    this.favoritesService?.getFavorites$()?.pipe(
      takeUntil(this.unsubscribe$),
      map((favorites: PersonalisationFavoriteData[]) => {
        return this.personalisationService.mapFavoritesFundIds(favorites);
      })
    );

  public getHeldProductsList$ = (
    capFirmLevelProducts?: number
  ): Observable<PersonalisationPersonalDataProduct[]> =>
    this.personalisationService.getPersonalDataWithProducts$().pipe(
      takeUntil(this.unsubscribe$),
      map(
        (
          personalData: PersonalisationPersonalDataWithProducts
        ): PersonalisationPersonalDataProduct[] =>
          personalData.hasPersonalHeldProducts
            ? personalData.products
            : personalData.products?.slice(0, capFirmLevelProducts)
      )
    );

  /**
   * reads fund nodes and transforms it into title/link pair
   * @param result search result
   */
  public getFundsFromResult(result: FundsSearchItem[]): SearchFundsItem[] {
    let fundsWithAllShareClasses: SearchFundsItem[];
    this.getFavorites$().subscribe(
      (validFunds: PersonalisationPersonalDataProduct[]) => {
        const fundResults = result.filter((item: FundsSearchItem) => {
          return !validFunds.some(
            (fund) =>
              fund.fundId === item._source.fundid &&
              fund.shareClassCode === item._source.shclcode
          );
        });
        fundsWithAllShareClasses = Array.from(
          fundResults
            .reduce(
              (mapItem, fundItem: FundsSearchItem) =>
                mapItem.set(fundItem._source.title, {
                  fundName: fundItem._source.title,
                  fundTitle: fundItem._source.title,
                  fundId: fundItem._source.fundid,
                  shareClassCode: fundItem._source.shclcode,
                  ticker: fundItem._source.ticker,
                  shareClasses: [
                    ...(mapItem.get(fundItem._source.title)?.shareClasses ??
                      []),
                    {
                      shareclass:
                        fundItem._source.shclcode === NA ||
                        fundItem._source.shclcode === SINGLECLASS
                          ? this.translateService.instant('products.add')
                          : fundItem._source.shclname,
                      ariaTitle: fundItem._source.shclname,
                      fundShareClassId:
                        fundItem._source.fundid +
                        '-' +
                        fundItem._source.shclcode,
                    },
                  ],
                }),
              new Map()
            )
            .values()
        );
      }
    );
    return fundsWithAllShareClasses;
  }

  /**
   * get fund data from pds and map them
   */
  public getProductDataForFavorite$(
    fundShareClassId: FundShareClassId
  ): Observable<ProductData> {
    return this.fundDataService
      .register(
        USServicingMyInvestmentsQuery,
        this.getVariables(fundShareClassId)
      )
      .pipe(map(this.mapDtoToProduct(fundShareClassId)));
  }

  /**
   * Add Locale To Url
   * @param url - string URL
   * @returns - string
   */
  public addLocaleToUrl(url: string): string {
    // This functionality is specyfic for Canada for now.
    // TODO: Check if this can be done with using multilingualMapping fromsite configuration.
    if (this.siteConfigService.isCanada()) {
      const currentChannel = this.appStateService.getChannel();
      if (!url.includes(currentChannel)) {
        url = `/${currentChannel}/${url}`.replace('//', '/');
      }
    }
    return url;
  }

  /**
   * map the pds data for the funds
   */
  private mapDtoToProduct = (fundShareClassId: FundShareClassId) => (
    productDetail: ApolloQueryResult<{
      Overview: ProductDTO;
    }>
  ) => {
    const productDto: ProductDTO = productDetail?.data?.Overview || null;

    // if no data, return null
    if (!productDto) {
      logger.warn(`No data found for fund: ${fundShareClassId}`);
      return null;
    }

    const mapperParams: MapperParams = {
      config: this.siteConfigService,
      includeSoftClosed: true,
      translateService: this.translateService,
    };

    logger.debug('productDto', productDto, productDetail, mapperParams);
    const product: Product = this.mapperFactory
      .createMapper(ProductMapper, mapperParams)
      .toDomain(productDto);
    const productData: ProductData = mapProductToProductData(product);
    return productData;
  };

  /**
   * variables needed to call pds req
   */
  private getVariables = (
    fundShareClassId: FundShareClassId
  ): Record<string, string> => ({
    countrycode: this.fundDataService.getCountry(),
    languagecode: this.fundDataService.getLanguage(),
    fundid: fundShareClassId.split('-')[0],
    shareclasscode: fundShareClassId.split('-')[1],
  });
}
